import { useEffect, useRef, useState } from "react";

// On-screen keyboard detection from the visual viewport. iOS never resizes the
// layout viewport for the keyboard: only `visualViewport.height` shrinks.
// Android shrinks both. Either way the visual viewport becomes much shorter
// than the tallest one seen at the same width, which is what this compares.
// Presentation only: nothing here reads or writes application state.

// Larger than any browser-chrome change (URL bar, tab bar), smaller than any
// software keyboard.
export const KEYBOARD_MIN_HEIGHT_PX = 150;

export function getVisualViewport(windowObject = typeof window === "undefined" ? null : window) {
  const viewport = windowObject?.visualViewport;
  return viewport && typeof viewport.addEventListener === "function" ? viewport : null;
}

// Tracks the tallest visual viewport per width (a rotation starts over) and
// reports whether the current one is keyboard-constrained.
export function createKeyboardTracker() {
  let width = null;
  let tallest = 0;
  return function measure(viewport) {
    if (!viewport) return { open: false, height: 0, tallest: 0 };
    const currentWidth = Math.round(viewport.width);
    // A pinch-zoomed viewport is short for another reason: never a keyboard.
    const zoomed = Math.abs(Number(viewport.scale || 1) - 1) > 0.01;
    if (width !== currentWidth) {
      width = currentWidth;
      tallest = 0;
    }
    if (!zoomed) tallest = Math.max(tallest, viewport.height);
    return {
      open: !zoomed && tallest - viewport.height > KEYBOARD_MIN_HEIGHT_PX,
      height: viewport.height,
      tallest,
    };
  };
}

// How much of the layout viewport's bottom the keyboard covers. iOS leaves the
// layout viewport (and `dvh`) at full height, so a bottom-anchored layer needs
// this to stay above the keyboard. Android resizes the layout viewport itself,
// which makes this 0 there.
export function keyboardInset(viewport, windowObject = window) {
  return coveredBottom(viewport, windowObject.innerHeight);
}

// How far `layoutBottom` (a bottom edge in layout-viewport coordinates, such as
// a fixed overlay's) lies below the bottom of what is actually visible.
export function coveredBottom(viewport, layoutBottom) {
  if (!viewport) return 0;
  const covered = Number(layoutBottom) - (viewport.offsetTop + viewport.height);
  return covered > 1 ? Math.round(covered) : 0;
}

export const STANDALONE_QUERY = "(display-mode: standalone)";

// The pixel height of a CSS length, read from a hidden probe that is removed
// in the same call. Null where the unit is unsupported or nothing is laid out.
export function measureCssHeight(value, documentObject = typeof document === "undefined" ? null : document) {
  if (!documentObject?.body) return null;
  const probe = documentObject.createElement("div");
  probe.style.cssText =
    "position:fixed;top:0;left:0;width:0;visibility:hidden;pointer-events:none;";
  probe.style.height = value;
  probe.setAttribute("data-lz-viewport-probe", value);
  documentObject.body.appendChild(probe);
  try {
    const height = probe.getBoundingClientRect().height;
    return height > 0 ? height : null;
  } finally {
    probe.remove();
  }
}

// Bottom edge of a layer sized from the installed app's large viewport
// (`100lvh`), or null outside an installed app. A layer sized that way must
// measure what the keyboard covers against this same edge: on iOS the layout
// viewport (`innerHeight`) can be shorter than the large viewport, and using
// it would undercount the covered height by the difference.
export function standaloneViewportBottom(windowObject = typeof window === "undefined" ? null : window) {
  if (!windowObject?.matchMedia?.(STANDALONE_QUERY)?.matches) return null;
  return measureCssHeight("100lvh", windowObject.document);
}

const CLOSED = Object.freeze({ open: false, inset: 0, offsetTop: 0 });

// How far iOS has panned the visual viewport down inside the layout viewport.
// It does that on its own when a focused field sits close to the keyboard, and
// a fixed layer positioned from the layout viewport then appears shifted up by
// the same amount. 0 when nothing is panned.
export function viewportOffsetTop(viewport) {
  const offset = Number(viewport?.offsetTop);
  return offset > 1 ? Math.round(offset) : 0;
}

// `{ open, inset, offsetTop }` for the software keyboard, from ONE
// visual-viewport subscription. `offsetTop` is the pan above (0 while the
// keyboard is closed). `getLayoutBottom` is the bottom edge of the layer being kept
// above the keyboard, in layout-viewport coordinates; it must be the same
// baseline that sizes that layer. Default: the window's layout viewport.
// Always closed where the visual viewport API is missing.
export function useKeyboardViewport({ enabled = true, getLayoutBottom = null } = {}) {
  const [state, setState] = useState(CLOSED);
  const layoutBottomRef = useRef(getLayoutBottom);
  layoutBottomRef.current = getLayoutBottom;

  useEffect(() => {
    const viewport = enabled ? getVisualViewport() : null;
    if (!viewport) {
      setState(CLOSED);
      return undefined;
    }
    const measure = createKeyboardTracker();
    const update = () => {
      const open = measure(viewport).open;
      const layoutBottom = open ? layoutBottomRef.current?.() : null;
      const inset = open
        ? coveredBottom(viewport, Number.isFinite(layoutBottom) ? layoutBottom : window.innerHeight)
        : 0;
      const offsetTop = open ? viewportOffsetTop(viewport) : 0;
      setState((current) => (
        current.open === open && current.inset === inset && current.offsetTop === offsetTop
          ? current
          : { open, inset, offsetTop }
      ));
    };
    update();
    viewport.addEventListener("resize", update);
    viewport.addEventListener("scroll", update);
    return () => {
      viewport.removeEventListener("resize", update);
      viewport.removeEventListener("scroll", update);
    };
  }, [enabled]);

  return state;
}

// True while the software keyboard is visibly open.
export default function useKeyboardOpen(options) {
  return useKeyboardViewport(options).open;
}
